<script setup>
import {ref, computed} from 'vue'
import {useRouter, useRoute} from 'vue-router'
import draggable from 'vuedraggable'
import {useComponentFieldsStore} from '@/stores/componentFields'
import {useComponentStructureStore} from '@/stores/componentStructure'
import {useItemDataStore} from '@/stores/itemData'
import DragHandle from '@/common/DragHandle'
import FieldTypeIcon from "./FieldTypeIcon.vue"
import Button from '@/common/Button.vue'

const router = useRouter()
const route = useRoute()

const componentFieldsStore = useComponentFieldsStore()
const componentStructureStore = useComponentStructureStore()
const itemDataStore = useItemDataStore()

// --- State ---

const isDragging = ref(false)
const ajaxInProgress = ref(false)
const dragImg = ref(null)

// Initialize blank drag image
const initDragImg = () => {
    const img = new Image()
    img.src = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw=='
    dragImg.value = img
}
initDragImg()

// --- Computed ---

const dragOptions = computed(() => ({
    animation: 200,
    ghostClass: 'ghost',
    setData: (dataTransfer) => {
        if (dragImg.value) {
            dataTransfer.setDragImage(dragImg.value, 0, 0)
        }
    }
}))

const isContentTypeComponent = computed(() => {
    return componentFieldsStore.isContentType
})

const selectedId = computed(() => {
    return isContentTypeComponent.value
        ? componentFieldsStore.activeEditId
        : parseInt(route.params.fieldId)
})

const items = computed({
    get: () => componentFieldsStore.items,
    set: (value) => {
        componentFieldsStore.items = JSON.parse(JSON.stringify(value))
    }
})

// --- Methods ---

const editItem = (id) => {
    if (isContentTypeComponent.value) {
        componentFieldsStore.activeEditId = id
    } else {
        router.push({
            name: 'componentFields',
            params: {
                fieldsetId: componentFieldsStore.activeFieldsetId || 0,
                fieldId: id
            }
        })
    }
}

const onDragEnd = (obj) => {
    isDragging.value = false
    const item = items.value[obj.newIndex]

    ajaxInProgress.value = true
    itemDataStore.update({
            tableName: 'modulestructure',
            id: item.id,
            data: {displayOrder: obj.newIndex + 1}
        })
        .then(() => {
            return componentStructureStore.init()
        })
        .finally(() => {
            ajaxInProgress.value = false
        })
}
</script>

<template>
    <div :class="{ 'content-type--items-is-dragging': isDragging }">
        <draggable
            v-bind="dragOptions"
            v-if="items.length"
            v-model="items"
            item-key="id"
            handle=".content-type--item-drag-handle"
            class="overflow-hidden"
            @start="isDragging = true"
            @end="onDragEnd"
        >
            <template #item="{ element: item }">
                <div>
                    <Button
                        :key="item.id"
                        ref="contentTypeItem"
                        :disabled="ajaxInProgress"
                        :active-class="'btn-primary'"
                        :active-value="item.id === selectedId"
                        class="ps-0 btn-sm w-100 d-flex align-items-top text-start"
                        @click.prevent="editItem(item.id)"
                    >
                        <DragHandle class="content-type--item-drag-handle"/>

                        <FieldTypeIcon :type="item.type" class="me-2"/>

                        {{ item.title }}
                    </Button>
                </div>
            </template>
        </draggable>
    </div>
</template>

<style scoped>
.ghost {
    background: #666 !important;
    border-color: #666 !important;
    color: #fff !important;
}

.content-type--item-drag-handle {
    opacity: 0;
    color: #999;
}

.btn:hover .content-type--item-drag-handle {
    opacity: 1;
}

.content-type--items-is-dragging .content-type--item-drag-handle {
    opacity: 0 !important;
}
</style>